import { useEffect, useRef, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { qrImageUrl } from '../hooks/usePersonalQrCode';
import { renderVisitorPassPng, savePassImage, isIosDevice } from '../utils/visitorPassImage';
import type { VisitorPassData } from '../utils/visitorPassImage';

/**
 * "Guardar pase" (ADDENDUM 2026-09-14, Item A): one-tap save of the offline
 * visitor pass PNG (static raw_code QR + text block) to the phone
 * gallery/downloads. UX states per spec:
 *  1. pre-save hint: "Funciona sin datos — muestra la imagen al guardia"
 *  2. saved confirmation (auto-dismiss), button stays as "Guardar de nuevo"
 *  3. re-save allowed, must not error
 *  4. share-not-supported fallback: long-press hint over the rendered image
 *  5. render failure: plain static QR image + screenshot caveat (last resort)
 *
 * The QR encoded is ALWAYS the static raw_code passed by the caller — never a
 * rotating VQR1 payload (those go stale within 60s).
 */

type SaveStatus = 'idle' | 'saving' | 'saved' | 'unsupported' | 'failed';

export default function SaveVisitorPass({ qrCode, pass, dark = false }: {
  qrCode: string;
  /** Fields for the text block (title = visitor name, lines = details). */
  pass: VisitorPassData;
  /** Match the dark-mode surfaces of MyQR cards. */
  dark?: boolean;
}) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [showSavedMsg, setShowSavedMsg] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const dismissTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
    if (fallbackUrl) URL.revokeObjectURL(fallbackUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (status === 'saving') return;
    setStatus('saving');
    // Decide the iOS path BEFORE any await: iOS Safari loses the user
    // activation while the canvas render loads its images, so navigator.share
    // fails with NotAllowedError and <a download> on blob: URLs is ignored
    // (live bug 2026-09-15). On iOS we show the long-press overlay directly.
    const iosDirect = isIosDevice();
    const blob = await renderVisitorPassPng({ ...pass, qrCode });
    if (!blob) {
      // State 5: canvas render failed — plain static QR + screenshot caveat.
      setFallbackUrl(null);
      setStatus('failed');
      return;
    }
    // Keep a display URL for the long-press fallback. On iOS prefer a data:
    // URL (long-press "Add to Photos" is most reliable on data: images, and it
    // survives any revocation timing); the temporary object URL is revoked
    // immediately. Elsewhere use a plain object URL as before.
    const objectUrl = URL.createObjectURL(blob);
    if (iosDirect) {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error);
        r.readAsDataURL(blob);
      }).catch(() => null);
      URL.revokeObjectURL(objectUrl);
      setFallbackUrl((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return dataUrl ?? objectUrl; // objectUrl only if dataURL failed
      });
    } else {
      setFallbackUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });
    }
    if (iosDirect) {
      // State 4 on iOS: inline image with long-press-to-save hint.
      setStatus('unsupported');
      return;
    }
    const result = await savePassImage(blob, pass.title || 'Visitante');
    if (result === 'cancelled') {
      // User dismissed the share sheet — stay ready, no error.
      setStatus('idle');
      return;
    }
    if (result === 'unsupported') {
      // State 4: show the image for long-press saving.
      setStatus('unsupported');
      return;
    }
    // States 2/3: confirmation (auto-dismiss), button remains as re-save.
    setStatus('saved');
    setShowSavedMsg(true);
    if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
    dismissTimer.current = window.setTimeout(() => setShowSavedMsg(false), 4000);
  };

  const label = status === 'saving' ? 'Guardando…' : status === 'saved' ? 'Guardar de nuevo' : 'Guardar pase';
  const hintCls = dark ? 'text-slate-400 dark:text-slate-400' : 'text-slate-500';

  return (
    <div>
      <button
        type="button"
        onClick={handleSave}
        disabled={status === 'saving'}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
        title="Guarda el QR estático del pase como imagen en tu galería/descargas"
      >
        {status === 'saving' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {label}
      </button>
      {status !== 'unsupported' && status !== 'failed' && (
        <p className={`text-xs mt-1.5 ${hintCls}`}>
          Funciona sin datos — muestra la imagen al guardia.
        </p>
      )}
      {status === 'saved' && showSavedMsg && (
        <p className={`text-xs mt-1.5 font-medium ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
          Pase guardado ✅ — encuéntralo en tu galería
        </p>
      )}
      {status === 'unsupported' && fallbackUrl && (
        <div className="mt-2">
          {/* White container: the pass is always dark-on-white (spec), even in
              dark mode — never inverted. */}
          <div className="bg-white border border-slate-200 rounded-lg p-2 flex justify-center">
            <img src={fallbackUrl} alt="Pase de visitante Vecindus" className="max-w-full h-auto rounded" />
          </div>
          <p className={`text-xs mt-1.5 ${hintCls}`}>
            Mantén presionada la imagen para guardarla.
          </p>
        </div>
      )}
      {status === 'failed' && (
        <div className="mt-2">
          <p className={`text-xs mb-1.5 ${dark ? 'text-amber-400' : 'text-amber-600'}`}>
            No pudimos generar el archivo. Toma una captura de pantalla de este QR — funciona igual (sujeto a vigencia).
          </p>
          <div className="bg-white border border-slate-200 rounded-lg p-3 flex justify-center">
            <img src={qrImageUrl(qrCode, 512)} alt="QR estático del pase" className="w-[220px] h-[220px]" />
          </div>
        </div>
      )}
    </div>
  );
}
